import { TAXONOMY_DISPLAY, TOPICS, TRADITIONS } from '@archetelos/lib';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { peekNext, takeNext } from '@/lib/auth/next';
import { useRequireAuth } from '@/lib/auth/useRequireAuth';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/onboarding/subscriber')({
  component: SubscriberOnboardingRoute,
});

function SubscriberOnboardingRoute() {
  const { user, refreshProfile } = useRequireAuth();
  const navigate = useNavigate();
  const [traditions, setTraditions] = useState<string[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<'never' | 'weekly' | 'monthly'>('weekly');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checkoutWaiting = peekNext().startsWith('/subscribe');

  const toggle = (list: string[], setter: (v: string[]) => void) => (value: string) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const submit = async (skip: boolean) => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      if (!skip) {
        const { error: prefError } = await supabase.from('subscriber_preferences').upsert(
          {
            user_id: user.id,
            preferred_traditions: traditions,
            preferred_topics: topics,
            email_digest_frequency: frequency,
          },
          { onConflict: 'user_id' },
        );
        if (prefError) throw prefError;
      }
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq('id', user.id);
      if (profileError) throw profileError;
      await refreshProfile();
      // Onboarding completion is where a carried next is finally consumed. With
      // no carried next, land on the member loop's front room ('/'), not the
      // deep subscriber home.
      void navigate({ to: takeNext() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment">
      <main className="mx-auto max-w-2xl px-6 py-16">
        <h1 className="font-serif text-3xl text-ink">A few preferences.</h1>
        <p className="mt-3 font-sans text-base text-ink/70 pretty">
          Optional. We use these to shape what shows up on your home and how often we write to you.
        </p>
        {checkoutWaiting && (
          <p className="mt-4 font-sans text-sm text-ink/60">
            Your checkout is waiting — this takes ten seconds.
          </p>
        )}

        <section className="mt-10">
          <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">
            Traditions you&apos;re drawn to
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {TRADITIONS.map((slug) => {
              const active = traditions.includes(slug);
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggle(traditions, setTraditions)(slug)}
                  className={`rounded-sm border px-3 py-1.5 text-sm transition-colors ${
                    active
                      ? 'border-oxblood bg-oxblood/10 text-oxblood'
                      : 'border-ink/15 text-ink hover:border-oxblood'
                  }`}
                  aria-pressed={active}
                >
                  {TAXONOMY_DISPLAY[slug] ?? slug}
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-10">
          <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">
            Topics that interest you
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {TOPICS.map((slug) => {
              const active = topics.includes(slug);
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggle(topics, setTopics)(slug)}
                  className={`rounded-sm border px-3 py-1.5 text-sm transition-colors ${
                    active
                      ? 'border-oxblood bg-oxblood/10 text-oxblood'
                      : 'border-ink/15 text-ink hover:border-oxblood'
                  }`}
                  aria-pressed={active}
                >
                  {TAXONOMY_DISPLAY[slug] ?? slug}
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-10">
          <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">
            Email digest frequency
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(['weekly', 'monthly', 'never'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setFrequency(option)}
                className={`rounded-sm border px-3 py-1.5 text-sm transition-colors ${
                  frequency === option
                    ? 'border-oxblood bg-oxblood/10 text-oxblood'
                    : 'border-ink/15 text-ink hover:border-oxblood'
                }`}
                aria-pressed={frequency === option}
              >
                {option[0]!.toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </section>

        {error && (
          <p role="alert" aria-live="polite" className="mt-6 text-sm text-signal">
            {error}
          </p>
        )}

        <div className="mt-12 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
          <button
            type="button"
            onClick={() => void submit(true)}
            disabled={saving}
            className="text-sm text-ink/60 hover:text-oxblood"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={() => void submit(false)}
            disabled={saving}
            className="rounded-md bg-ink px-6 py-2.5 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Enter ArcheTelos'}
          </button>
        </div>
      </main>
    </div>
  );
}
